import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { isProActive } from "@/lib/featureAccess";
import { useEntitlement } from "@/lib/entitlement";
import UpgradeScreen from "@/components/UpgradeScreen";

// Kill Sheet Print View — IWCF-style 2-page printable layout (P4).
//
// Visually mirrors the IWCF Surface BOP Kill Sheet reference image:
//   PAGE 1 — Formation Strength | Current Well | Pre-Recorded Volumes
//   PAGE 2 — Kick Data | Kill Density/Gradient | ICP/FCP | Step-Down + Graph
//
// Print requirements satisfied:
//   #3 B&W readable — no colour reliance. All cells use black borders + dark
//       text on white. Amber accent reserved for screen preview only and
//       suppressed via @media print.
//   #4 Letter + A4 — @page { size: auto } lets the browser handle both;
//       layout uses inches with safe margins that fit both 8.5×11 and A4.
//   #5 Footer carries generation timestamp + profile snapshot version refs.
//   #6 Operational-use disclaimer printed at the bottom of every page.

const PRINT_CSS = `
@page { size: auto; margin: 12mm; }
@media print {
  body { background: white !important; }
  .ks-print { color: #000 !important; background: #fff !important; }
  .ks-print * { color: inherit !important; background: transparent !important; border-color: #000 !important; }
  .ks-page { break-after: page; }
  .ks-page:last-child { break-after: auto; }
  .no-print { display: none !important; }
}
.ks-print { font-family: "Times New Roman", Times, serif; color: #000; background: #fff; }
.ks-print h1, .ks-print h2, .ks-print h3 { font-family: "Helvetica", Arial, sans-serif; }
.ks-print .ks-page { width: 7.0in; min-height: 9.5in; margin: 0 auto 24px; padding: 8mm; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.15); position: relative; }
.ks-print .ks-table { border: 1px solid #000; border-collapse: collapse; width: 100%; }
.ks-print .ks-table th, .ks-print .ks-table td { border: 1px solid #000; padding: 3px 6px; font-size: 10.5px; vertical-align: top; }
.ks-print .ks-table th { background: #e6e6e6 !important; font-weight: bold; }
.ks-print .ks-box { border: 1px solid #000; padding: 4px 6px; font-size: 10.5px; }
.ks-print .ks-cell { display: inline-block; min-width: 60px; padding: 1px 6px; border: 1px solid #000; background: #f2f2f2; font-family: "Courier New", monospace; }
.ks-print .ks-header { display: grid; grid-template-columns: 1.6fr 1.4fr 0.8fr; border: 1px solid #000; }
.ks-print .ks-header > div { border-right: 1px solid #000; padding: 4px 8px; font-size: 11px; }
.ks-print .ks-header > div:last-child { border-right: none; }
.ks-print .ks-mono { font-family: "Courier New", monospace; }
.ks-print .ks-disclaimer { border-top: 1px solid #000; padding-top: 4px; margin-top: 8px; font-size: 8.5px; line-height: 1.3; }
.ks-print .ks-footer { position: absolute; bottom: 8mm; left: 8mm; right: 8mm; font-size: 8px; color: #444; border-top: 1px solid #000; padding-top: 4px; display: flex; justify-content: space-between; gap: 8px; }
`;

export default function KillSheetPrint() {
  const { id } = useParams();
  const entitlement = useEntitlement();
  const [data, setData] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`rsc.killsheet.print.${id}`);
      if (raw) setData(JSON.parse(raw));
    } catch { setData(null); }
  }, [id]);

  // Printed output is Pro-only, same as the Results section in
  // KillSheetEditor.jsx/KillSheetWizard.jsx. Checked here too because the
  // print payload lives in sessionStorage, which can outlive an
  // entitlement change within the same browser tab (e.g. a subscription
  // lapses after a Pro user already opened this print view once).
  //
  // Uses the reactive hook, NOT getCachedEntitlement(): this page opens via
  // window.open() into a brand-new tab, where the module-level cache is
  // still the fail-closed `loading: true` shape. isProActive() returns
  // false on `loading` before it ever reaches the isOwner/isPro checks, and
  // the plain cache getter never re-renders when the fetch lands — so the
  // paywall stuck permanently for Owner and Pro users alike. Waiting for
  // the load mirrors RequireProAccess.jsx; Free users are unchanged.
  if (entitlement.loading) return null;
  if (!isProActive(entitlement)) {
    return <UpgradeScreen context="Kill Sheet printing & export" />;
  }

  if (!data) {
    return <div style={{ padding: 24 }}>Print payload not found. Open the printable view from the wizard.</div>;
  }

  const { session, output } = data;
  const o = output;
  const u = o.meta.units;
  const isSI = u === "SI";
  const unitPress = isSI ? "kPa"   : "psi";
  const unitDen   = isSI ? "kg/m³" : "ppg";
  const unitVol   = isSI ? "m³"    : "bbl";
  const unitLen   = isSI ? "m"     : "ft";

  return (
    <div className="ks-print">
      <style>{PRINT_CSS}</style>
      <div className="no-print" style={{ padding: "12px 16px", background: "#0A0B0E", color: "#9aa6b2", fontSize: 12 }}>
        <strong>Preview</strong> — press <kbd>Ctrl/Cmd + P</kbd> to print or Save as PDF.
        Letter and A4 paper sizes both supported via the page's CSS @page rule.
      </div>

      <Page1 o={o} session={session} unitPress={unitPress} unitDen={unitDen} unitVol={unitVol} unitLen={unitLen} />
      <Page2 o={o} session={session} unitPress={unitPress} unitDen={unitDen} />
    </div>
  );
}

function PageFooter({ o }) {
  const refs = o.meta.refs || {};
  const refStr = Object.entries(refs)
    .filter(([, v]) => v && v.id)
    .map(([k, v]) => `${k}#${v.id}@v${v.version}`)
    .join(" · ");
  return (
    <>
      <div className="ks-disclaimer">
        <strong>DISCLAIMER — calculation &amp; documentation support only.</strong>{" "}
        This kill sheet is a deterministic computation aid based on operator-supplied
        inputs. It is <strong>not</strong> a substitute for a certified
        well-control supervisor's judgment or for a duly-licensed kill-sheet
        procedure. All outputs must be independently verified against the rig's
        official records and well-control plan before any operational use.
      </div>
      <div className="ks-footer">
        <span>RigSentinel · generated {new Date(o.meta.generatedAt).toLocaleString()}</span>
        <span className="ks-mono">{refStr}</span>
        <span>Session {o.meta.sessionId || "—"}</span>
      </div>
    </>
  );
}

function Page1({ o, session, unitPress, unitDen, unitVol, unitLen }) {
  const fs = o.formationStrength;
  const cw = o.currentWell;
  const v = o.volumes;
  const ps = o.pumpStrokes;
  return (
    <section className="ks-page">
      <div className="ks-header">
        <div><strong>{session.variant.startsWith("subsea-") ? "Subsea BOP" : "Surface BOP"}</strong></div>
        <div style={{ textAlign: "center" }}><strong>KILL SHEET</strong></div>
        <div style={{ textAlign: "right" }}>1 of 2</div>
      </div>
      <table className="ks-table" style={{ marginTop: 6 }}>
        <tbody>
          <tr>
            <td><strong>WELLNAME:</strong> <span className="ks-cell">{o.meta.wellName || "—"}</span></td>
            <td><strong>UNITS:</strong> <span className="ks-cell">{o.meta.units}</span></td>
            <td><strong>DATE:</strong> <span className="ks-cell">{o.meta.date}</span></td>
          </tr>
        </tbody>
      </table>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 6, marginTop: 6 }}>
        {/* FORMATION STRENGTH */}
        <div className="ks-box">
          <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>FORMATION STRENGTH DATA</h3>
          <div>Surface LOT (A): <span className="ks-cell">{fs.lotPressure.display ?? "—"}</span> {unitPress}</div>
          <div>MW @ test (B): <span className="ks-cell">{fs.mwAtTest.display ?? "—"}</span> {unitDen}</div>
          <div style={{ marginTop: 4 }}>
            Max MW (C) = (A)/g/ShoeTVD + (B) =&nbsp;
            <span className="ks-cell">{fs.maxMudDensity.display ?? "—"}</span> {unitDen}
          </div>
          <div style={{ marginTop: 4 }}>
            Initial MAASP = ((C)−ρ)×ShoeTVD×g =&nbsp;
            <span className="ks-cell">{fs.maaspInitial.display ?? "—"}</span> {unitPress}
          </div>
          <div style={{ marginTop: 8 }}>
            <strong>Pump Displacement:</strong>{" "}
            <span className="ks-cell">{o.pump.displacement.display ?? "—"}</span> {isSIUnit(o) ? "m³/stk" : "bbl/stk"}
          </div>
          <div>SPM: <span className="ks-cell">{o.pump.slowPumpRate.display ?? "—"}</span></div>
          <div>Slow Pump P (DPL): <span className="ks-cell">{o.pump.slowPumpPressure.display ?? "—"}</span> {unitPress}</div>
        </div>

        {/* CURRENT WELL */}
        <div className="ks-box">
          <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>CURRENT WELL DATA</h3>
          <div><strong>DRILLING FLUID</strong></div>
          <div>Density: <span className="ks-cell">{cw.mudDensity.display ?? "—"}</span> {unitDen}</div>
          <div>Gradient: <span className="ks-cell">{cw.mudGradient.display ?? "—"}</span> {unitPress}/{unitLen}</div>
          <div style={{ marginTop: 6 }}><strong>CASING &amp; SHOE</strong></div>
          <div>Shoe MD: <span className="ks-cell">{cw.shoeMd.display ?? "—"}</span> {unitLen}</div>
          <div>Shoe TVD: <span className="ks-cell">{cw.shoeTvd.display ?? "—"}</span> {unitLen}</div>
          <div style={{ marginTop: 6 }}><strong>HOLE</strong></div>
          <div>Hole Size: <span className="ks-cell">{cw.holeSize.display ?? "—"}</span></div>
          <div>MD: <span className="ks-cell">{cw.md.display ?? "—"}</span> {unitLen}</div>
          <div>TVD: <span className="ks-cell">{cw.tvd.display ?? "—"}</span> {unitLen}</div>
        </div>

        {/* PRE-RECORDED VOLUMES */}
        <div className="ks-box">
          <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>PRE-RECORDED VOLUMES</h3>
          <table className="ks-table" style={{ borderColor: "#000" }}>
            <thead>
              <tr><th>Section</th><th>L ({unitLen})</th><th>Cap</th><th>Vol ({unitVol})</th></tr>
            </thead>
            <tbody>
              {v.sections.map((s, i) => (
                <tr key={i}>
                  <td>{s.label}</td>
                  <td className="ks-mono">{s.length ?? "—"}</td>
                  <td className="ks-mono">{s.capacity ?? "—"}</td>
                  <td className="ks-mono">{s.volume.display ?? "—"}</td>
                </tr>
              ))}
              <tr><td><strong>(D) DSV</strong></td><td colSpan={2} /><td className="ks-mono">{v.drillStringVolume.display}</td></tr>
              <tr><td><strong>(F) OH</strong></td><td colSpan={2} /><td className="ks-mono">{v.openHoleVolume.display}</td></tr>
              <tr><td><strong>(G) Csg</strong></td><td colSpan={2} /><td className="ks-mono">{v.inCasingVolume.display}</td></tr>
              <tr><td><strong>(H)=F+G Annulus</strong></td><td colSpan={2} /><td className="ks-mono">{v.totalAnnulusVolume.display}</td></tr>
              <tr><td><strong>(I)=D+H Well Sys</strong></td><td colSpan={2} /><td className="ks-mono">{v.totalWellSystem.display}</td></tr>
              <tr><td><strong>(J) Active Surface</strong></td><td colSpan={2} /><td className="ks-mono">{v.activeSurface.display}</td></tr>
              <tr><td><strong>(I+J) Active Fluid</strong></td><td colSpan={2} /><td className="ks-mono">{v.totalActiveFluid.display}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <table className="ks-table" style={{ marginTop: 6 }}>
        <thead><tr><th>Section</th><th>Strokes</th><th>Time (min)</th></tr></thead>
        <tbody>
          {[
            ["(E) Drill String", ps.dsv],
            ["Open Hole",        ps.openHole],
            ["In Casing",        ps.inCasing],
            ["Total Annulus",    ps.totalAnnulus],
            ["Total Well Sys",   ps.totalWellSystem],
            ["Surface Line",     ps.surfaceLine],
          ].map(([lbl, r]) => (
            <tr key={lbl}><td>{lbl}</td><td className="ks-mono">{r.strokes.display ?? "—"}</td><td className="ks-mono">{r.time.display ?? "—"}</td></tr>
          ))}
          <tr>
            <td><strong>Total Active Fluid</strong></td>
            <td className="ks-mono">
              {ps.totalActiveFluid.strokesIwcfPadded.display ?? "—"}
              <sup>*</sup>
            </td>
            <td className="ks-mono">{ps.totalActiveFluid.time.display ?? "—"}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ fontSize: 8.5, marginTop: 2 }}>
        <sup>*</sup> IWCF safety-pad convention (+1 stroke over math result {ps.totalActiveFluid.strokes.display}).
      </div>

      <PageFooter o={o} />
    </section>
  );
}

function Page2({ o, session, unitPress, unitDen }) {
  const k = o.killCalc;
  const a = o.additionalOutputs;
  const sd = k.stepDown;
  return (
    <section className="ks-page">
      <div className="ks-header">
        <div><strong>{session.variant.startsWith("subsea-") ? "Subsea BOP" : "Surface BOP"}</strong></div>
        <div style={{ textAlign: "center" }}><strong>KILL SHEET</strong></div>
        <div style={{ textAlign: "right" }}>2 of 2</div>
      </div>

      <div className="ks-box" style={{ marginTop: 6 }}>
        <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>KICK DATA</h3>
        <div>SIDPP: <span className="ks-cell">{o.kickData.sidpp.display ?? "—"}</span> {unitPress}</div>
        <div>SICP:  <span className="ks-cell">{o.kickData.sicp.display ?? "—"}</span> {unitPress}</div>
        <div>Pit Gain: <span className="ks-cell">{o.kickData.pitGain.display ?? "—"}</span></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 6 }}>
        <div className="ks-box">
          <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>KILL FLUID DENSITY</h3>
          <div className="ks-mono" style={{ fontSize: 10 }}>= SIDPP / TVD / g + ρ</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>
            <span className="ks-cell">{k.kmd.display ?? "—"}</span> {unitDen}
          </div>
        </div>
        <div className="ks-box">
          <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>KILL FLUID GRADIENT</h3>
          <div className="ks-mono" style={{ fontSize: 10 }}>= CMG + SIDPP / TVD</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>
            <span className="ks-cell">{k.kmg.display ?? "—"}</span> {unitPress}/m
          </div>
        </div>
        <div className="ks-box">
          <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>INITIAL CIRC PRESSURE (ICP)</h3>
          <div className="ks-mono" style={{ fontSize: 10 }}>= SIDPP + DPL</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>
            <span className="ks-cell">{k.icp.display ?? "—"}</span> {unitPress}
          </div>
        </div>
        <div className="ks-box">
          <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>FINAL CIRC PRESSURE (FCP)</h3>
          <div className="ks-mono" style={{ fontSize: 10 }}>= (KMG/CMG) × DPL</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>
            <span className="ks-cell">{k.fcp.display ?? "—"}</span> {unitPress}
          </div>
        </div>
      </div>

      <div className="ks-box" style={{ marginTop: 6 }}>
        K = (ICP − FCP) × 100 / (E) =&nbsp;
        <span className="ks-cell">{k.kPer100.display ?? "—"}</span> {unitPress}/100 stks
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 6, marginTop: 6 }}>
        <table className="ks-table">
          <thead><tr><th>STROKES</th><th>PRESSURE ({unitPress})</th></tr></thead>
          <tbody>
            {sd.map((r, i) => (
              <tr key={i}><td className="ks-mono">{r.strokes.display}</td><td className="ks-mono">{r.pressure.display}</td></tr>
            ))}
          </tbody>
        </table>
        <DppGraph stepDown={sd} icp={k.icp.display} fcp={k.fcp.display} unitPress={unitPress} />
      </div>

      <div className="ks-box" style={{ marginTop: 6 }}>
        <h3 style={{ fontSize: 11, margin: "0 0 4px" }}>ADDITIONAL OUTPUTS</h3>
        <div>MAASP (new w/ KMD): <span className="ks-cell">{a.maaspNew.display ?? "—"}</span> {unitPress}</div>
        <div>Formation Pressure / BHP: <span className="ks-cell">{a.formationPressure.display ?? "—"}</span> {unitPress}</div>
        <div>Fracture Margin (MAASP − SICP): <span className="ks-cell">{a.fractureMargin.display ?? "—"}</span> {unitPress}</div>
        {a.killMargin && (
          <div>Kill Margin: <span className="ks-cell">{a.killMargin.density.display ?? "—"}</span> {unitDen}{" / "}
            <span className="ks-cell">{a.killMargin.pressure.display ?? "—"}</span> {unitPress}</div>
        )}
      </div>

      <PageFooter o={o} />
    </section>
  );
}

function DppGraph({ stepDown, icp, fcp, unitPress }) {
  if (!stepDown || stepDown.length < 2) return null;
  const W = 320, H = 180, pad = 28;
  const xs = stepDown.map((r) => r.strokes.display ?? 0);
  const ys = stepDown.map((r) => r.pressure.display ?? 0);
  const xMin = 0, xMax = Math.max(...xs) || 1;
  const yMin = Math.min(...ys) || 0, yMax = Math.max(...ys) || 1;
  const sx = (x) => pad + ((x - xMin) / (xMax - xMin || 1)) * (W - 2 * pad);
  const sy = (y) => H - pad - ((y - yMin) / (yMax - yMin || 1)) * (H - 2 * pad);
  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${sx(x)} ${sy(ys[i])}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", border: "1px solid #000", background: "#fff" }}>
      <title>Static &amp; Dynamic Drill Pipe Pressure</title>
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#000" />
      <line x1={pad} y1={pad}     x2={pad} y2={H - pad} stroke="#000" />
      <path d={path} fill="none" stroke="#000" strokeWidth="1.5" />
      <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="9" fill="#000">STROKES</text>
      <text x={6} y={H / 2} fontSize="9" fill="#000" transform={`rotate(-90 6 ${H / 2})`}>{unitPress}</text>
      <text x={sx(0)}     y={sy(icp) - 4} fontSize="9" fill="#000">ICP {icp}</text>
      <text x={sx(xMax)}  y={sy(fcp) + 12} textAnchor="end" fontSize="9" fill="#000">FCP {fcp}</text>
    </svg>
  );
}

function isSIUnit(o) { return o.meta.units === "SI"; }
